/* ==========================================================================
   Timeline System - The Arcanopolis Archive
   Interactive timeline visualization using vis-timeline
   ========================================================================== */

const TimelineSystem = {
    timeline: null,
    timelineData: [],
    timelineGroups: [],
    
    // Initialize timeline system
    init() {
        this.setupTimelineInterface();
        this.loadTimelineData();
        this.createTimeline();
        this.setupEventHandlers();
    },
    
    // Setup timeline interface
    setupTimelineInterface() {
        // Timeline container
        const timelineContainer = document.getElementById('timelineContainer');
        if (!timelineContainer) {
            console.error('Timeline container not found');
            return;
        }
        
        // Create timeline element
        timelineContainer.innerHTML = '<div id="timeline"></div>';
    },
    
    // Load timeline data from content
    loadTimelineData() {
        const allItems = [];
        for (const [category, items] of App.data) {
            allItems.push(...items.map(item => ({ ...item, category })));
        }
        
        // Filter items that have timeline data
        const timelineItems = allItems.filter(item => 
            item.timeline_date && item.event_type
        );
        
        // Convert to vis-timeline format
        this.timelineData = timelineItems.map(item => ({
            id: item.id,
            content: item.title,
            start: this.parseTimelineDate(item.timeline_date),
            group: item.event_type,
            className: `timeline-item ${item.category}`,
            title: this.createTooltip(item),
            data: item
        }));
        
        // Create groups based on event types
        const eventTypes = [...new Set(timelineItems.map(item => item.event_type))];
        this.timelineGroups = eventTypes.map(eventType => ({
            id: eventType,
            content: this.formatEventType(eventType),
            className: `timeline-group ${eventType}`
        }));
        
        console.log(`📅 Loaded ${this.timelineData.length} timeline events`);
    },
    
    // Parse timeline date
    parseTimelineDate(dateString) {
        // Handle various date formats
        if (typeof dateString === 'number') {
            // Assume it's a year
            return new Date(dateString, 0, 1);
        }
        
        if (typeof dateString === 'string') {
            // Try to parse as date
            const date = new Date(dateString);
            if (!isNaN(date.getTime())) {
                return date;
            }
            
            // Try to extract year from string
            const yearMatch = dateString.match(/\d{4}/);
            if (yearMatch) {
                return new Date(parseInt(yearMatch[0]), 0, 1);
            }
        }
        
        // Fallback to current date
        return new Date();
    },
    
    // Format event type for display
    formatEventType(eventType) {
        const typeMap = {
            'founding': '🏛️ Founding',
            'conflict': '⚔️ Conflict',
            'discovery': '🔍 Discovery',
            'political': '🏛️ Political',
            'cultural': '🎭 Cultural',
            'magical': '✨ Magical',
            'catastrophe': '💥 Catastrophe',
            'alliance': '🤝 Alliance',
            'research': '🔬 Research'
        };
        
        return typeMap[eventType] || `📅 ${eventType}`;
    },
    
    // Create tooltip content
    createTooltip(item) {
        let tooltip = `<strong>${item.title}</strong>`;
        
        if (item.category) {
            tooltip += `<br><em>${App.getCategoryLabel(item.category)}</em>`;
        }
        
        if (item.tags && item.tags.length > 0) {
            tooltip += `<br>Tags: ${item.tags.join(', ')}`;
        }
        
        if (item.rawContent) {
            const excerpt = item.rawContent.substring(0, 200);
            tooltip += `<br><br>${excerpt}${excerpt.length >= 200 ? '...' : ''}`;
        }
        
        return tooltip;
    },
    
    // Create timeline visualization
    createTimeline() {
        const timelineElement = document.getElementById('timeline');
        if (!timelineElement) {
            console.error('Timeline element not found');
            return;
        }
        
        // Timeline options
        const options = {
            stack: false,
            orientation: 'top',
            zoomable: true,
            moveable: true,
            selectable: true,
            multiselect: false,
            showCurrentTime: false,
            showMajorLabels: true,
            showMinorLabels: true,
            tooltip: {
                followMouse: true,
                overflowMethod: 'cap'
            },
            format: {
                minorLabels: {
                    millisecond: 'SSS',
                    second: 's',
                    minute: 'HH:mm',
                    hour: 'HH:mm',
                    weekday: 'ddd D',
                    day: 'D',
                    week: 'w',
                    month: 'MMM',
                    year: 'YYYY'
                },
                majorLabels: {
                    millisecond: 'HH:mm:ss',
                    second: 'D MMMM HH:mm',
                    minute: 'ddd D MMMM',
                    hour: 'ddd D MMMM',
                    weekday: 'MMMM YYYY',
                    day: 'MMMM YYYY',
                    week: 'MMMM YYYY',
                    month: 'YYYY',
                    year: ''
                }
            },
            groupOrder: 'content',
            groupOrderSwap: (a, b, groups) => {
                const order = ['founding', 'conflict', 'discovery', 'political', 'cultural', 'magical', 'catastrophe', 'alliance', 'research'];
                const aIndex = order.indexOf(a.content);
                const bIndex = order.indexOf(b.content);
                return aIndex - bIndex;
            }
        };
        
        // Create timeline
        this.timeline = new vis.Timeline(timelineElement, this.timelineData, this.timelineGroups, options);
        
        // Set initial zoom
        if (this.timelineData.length > 0) {
            const dates = this.timelineData.map(item => item.start);
            const minDate = new Date(Math.min(...dates));
            const maxDate = new Date(Math.max(...dates));
            
            // Add some padding
            const padding = (maxDate - minDate) * 0.1;
            this.timeline.setWindow(minDate - padding, maxDate + padding);
        }
    },
    
    // Setup event handlers
    setupEventHandlers() {
        if (!this.timeline) return;
        
        // Item click handler
        this.timeline.on('select', (properties) => {
            if (properties.items.length > 0) {
                const itemId = properties.items[0];
                const item = this.timelineData.find(d => d.id === itemId);
                if (item && item.data) {
                    this.openContent(item.data);
                }
            }
        });
        
        // Double-click to open content
        this.timeline.on('doubleClick', (properties) => {
            if (properties.item) {
                const item = this.timelineData.find(d => d.id === properties.item);
                if (item && item.data) {
                    this.openContent(item.data);
                }
            }
        });
        
        // Setup filter controls
        this.setupFilterControls();
    },
    
    // Setup filter controls
    setupFilterControls() {
        // Event type filter
        const eventTypeFilter = document.getElementById('eventTypeFilter');
        if (eventTypeFilter) {
            // Populate filter options
            const eventTypes = [...new Set(this.timelineData.map(item => item.group))];
            eventTypeFilter.innerHTML = '<option value="all">All Event Types</option>' +
                eventTypes.map(type => 
                    `<option value="${type}">${this.formatEventType(type)}</option>`
                ).join('');
            
            eventTypeFilter.addEventListener('change', () => {
                this.filterByEventType(eventTypeFilter.value);
            });
        }
        
        // Category filter
        const categoryFilter = document.getElementById('categoryFilter');
        if (categoryFilter) {
            const categories = [...new Set(this.timelineData.map(item => item.data.category))];
            categoryFilter.innerHTML = '<option value="all">All Categories</option>' +
                categories.map(category => 
                    `<option value="${category}">${App.getCategoryLabel(category)}</option>`
                ).join('');
            
            categoryFilter.addEventListener('change', () => {
                this.filterByCategory(categoryFilter.value);
            });
        }
        
        // Date range filter
        const dateFrom = document.getElementById('dateFrom');
        const dateTo = document.getElementById('dateTo');
        
        if (dateFrom) {
            dateFrom.addEventListener('change', () => {
                this.filterByDateRange();
            });
        }
        
        if (dateTo) {
            dateTo.addEventListener('change', () => {
                this.filterByDateRange();
            });
        }
        
        // Zoom controls
        this.setupZoomControls();
    },
    
    // Setup zoom controls
    setupZoomControls() {
        const zoomIn = document.getElementById('zoomIn');
        const zoomOut = document.getElementById('zoomOut');
        const zoomFit = document.getElementById('zoomFit');
        
        if (zoomIn) {
            zoomIn.addEventListener('click', () => {
                this.timeline.zoomIn(0.2);
            });
        }
        
        if (zoomOut) {
            zoomOut.addEventListener('click', () => {
                this.timeline.zoomOut(0.2);
            });
        }
        
        if (zoomFit) {
            zoomFit.addEventListener('click', () => {
                this.fitTimeline();
            });
        }
    },
    
    // Filter by event type
    filterByEventType(eventType) {
        if (eventType === 'all') {
            this.timeline.setItems(this.timelineData);
        } else {
            const filtered = this.timelineData.filter(item => item.group === eventType);
            this.timeline.setItems(filtered);
        }
    },
    
    // Filter by category
    filterByCategory(category) {
        if (category === 'all') {
            this.timeline.setItems(this.timelineData);
        } else {
            const filtered = this.timelineData.filter(item => item.data.category === category);
            this.timeline.setItems(filtered);
        }
    },
    
    // Filter by date range
    filterByDateRange() {
        const dateFrom = document.getElementById('dateFrom');
        const dateTo = document.getElementById('dateTo');
        
        if (!dateFrom || !dateTo) return;
        
        const fromDate = dateFrom.value ? new Date(dateFrom.value) : null;
        const toDate = dateTo.value ? new Date(dateTo.value) : null;
        
        let filtered = this.timelineData;
        
        if (fromDate) {
            filtered = filtered.filter(item => item.start >= fromDate);
        }
        
        if (toDate) {
            filtered = filtered.filter(item => item.start <= toDate);
        }
        
        this.timeline.setItems(filtered);
    },
    
    // Fit timeline to show all items
    fitTimeline() {
        if (this.timelineData.length === 0) return;
        
        const dates = this.timelineData.map(item => item.start);
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        
        // Add some padding
        const padding = (maxDate - minDate) * 0.1;
        this.timeline.setWindow(minDate - padding, maxDate + padding);
    },
    
    // Open content in new page
    openContent(item) {
        window.open(`content.html?id=${item.id}`, '_blank');
    },
    
    // Update timeline with new data
    updateTimeline() {
        this.loadTimelineData();
        if (this.timeline) {
            this.timeline.setItems(this.timelineData);
            this.timeline.setGroups(this.timelineGroups);
        }
    },
    
    // Get timeline statistics
    getTimelineStats() {
        const stats = {
            totalEvents: this.timelineData.length,
            eventTypes: {},
            categories: {},
            dateRange: {
                earliest: null,
                latest: null
            }
        };
        
        if (this.timelineData.length === 0) {
            return stats;
        }
        
        // Count by event type
        this.timelineData.forEach(item => {
            stats.eventTypes[item.group] = (stats.eventTypes[item.group] || 0) + 1;
        });
        
        // Count by category
        this.timelineData.forEach(item => {
            const category = item.data.category;
            stats.categories[category] = (stats.categories[category] || 0) + 1;
        });
        
        // Date range
        const dates = this.timelineData.map(item => item.start);
        stats.dateRange.earliest = new Date(Math.min(...dates));
        stats.dateRange.latest = new Date(Math.max(...dates));
        
        return stats;
    }
};

// Export for use in other modules
window.TimelineSystem = TimelineSystem;